import React from "react";
import { Link, useParams } from "react-router-dom";

import { blogApi } from "../api/blogApi";
import { filesApi } from "../api/filesApi";
import { useAuth } from "../auth/authStore";
import { useCreateSectionGate } from "../subscription/useCreateSectionGate";
import brandLogo from "../assets/img/logo.svg";

const CATEGORY_OPTIONS = [
  { value: "Тревожность", emoji: "😰", tag: "Тревога", tagClass: "ot-lav" },
  { value: "Выгорание", emoji: "🔥", tag: "Стресс", tagClass: "ot-amber" },
  { value: "Отношения", emoji: "💬", tag: "Пары", tagClass: "ot-pink" },
  { value: "Самооценка", emoji: "🌱", tag: "Рост", tagClass: "ot-mint" },
  { value: "Травма", emoji: "🧩", tag: "КПТ", tagClass: "ot-coral" },
  { value: "Родительство", emoji: "🌙", tag: "Семья", tagClass: "ot-amber" },
  { value: "Для специалистов", emoji: "🎓", tag: "Профи", tagClass: "ot-grey" },
];

const MATERIAL_TYPES = [
  { value: "Статья", emoji: "📝" },
  { value: "Заметка", emoji: "💭" },
  { value: "Практика", emoji: "🧘" },
  { value: "Исследование", emoji: "🔬" },
  { value: "Интервью", emoji: "🎙" },
];

const TRANSLIT_MAP = {
  а: "a",
  б: "b",
  в: "v",
  г: "g",
  д: "d",
  е: "e",
  ё: "yo",
  ж: "zh",
  з: "z",
  и: "i",
  й: "j",
  к: "k",
  л: "l",
  м: "m",
  н: "n",
  о: "o",
  п: "p",
  р: "r",
  с: "s",
  т: "t",
  у: "u",
  ф: "f",
  х: "kh",
  ц: "ts",
  ч: "ch",
  ш: "sh",
  щ: "shch",
  ы: "y",
  э: "e",
  ю: "yu",
  я: "ya",
};

const MOSCOW_TZ = "Europe/Moscow";
const MOSCOW_OFFSET = "+03:00";
const BLOG_TITLE_MAX = 255;
const BLOG_EXCERPT_MAX = 500;
const SEO_TITLE_MAX = 70;
const SEO_DESC_MAX = 160;
const SEO_SLUG_MAX = 255;

function toLocalDatetimeValue(date = new Date()) {
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return "";
  const parts = new Intl.DateTimeFormat("ru-RU", {
    timeZone: MOSCOW_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  })
    .formatToParts(d)
    .reduce((acc, part) => {
      if (part.type !== "literal") acc[part.type] = part.value;
      return acc;
    }, {});

  const yyyy = String(parts.year || "");
  const mm = String(parts.month || "").padStart(2, "0");
  const dd = String(parts.day || "").padStart(2, "0");
  const hh = String(parts.hour || "").padStart(2, "0");
  const mi = String(parts.minute || "").padStart(2, "0");
  if (!yyyy || !mm || !dd || !hh || !mi) return "";
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
}

function defaultSchedule() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(10, 0, 0, 0);
  return toLocalDatetimeValue(d);
}

function toMoscowIsoDateTime(value = "") {
  const raw = String(value || "").trim();
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
  if (!match) return null;
  const [, year, month, day, hour, minute] = match;
  return `${year}-${month}-${day}T${hour}:${minute}:00${MOSCOW_OFFSET}`;
}

function slugify(value = "") {
  const transliterated = String(value || "")
    .trim()
    .toLowerCase()
    .split("")
    .map((char) => TRANSLIT_MAP[char] || char)
    .join("");

  return transliterated
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 60);
}

function normalizeSeoSlug(value = "") {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, SEO_SLUG_MAX);
}

function extractTextFromBlocks(editorData) {
  if (!editorData || !Array.isArray(editorData.blocks)) return "";

  const chunks = [];
  editorData.blocks.forEach((block) => {
    const type = String(block?.type || "").toLowerCase();
    const data = block?.data || {};

    if (type === "paragraph" || type === "header") {
      chunks.push(String(data.text || "").replace(/<[^>]+>/g, " "));
      return;
    }

    if (type === "quote") {
      chunks.push(String(data.text || "").replace(/<[^>]+>/g, " "));
      return;
    }

    if (type === "list") {
      const walk = (items) => {
        if (!Array.isArray(items)) return;
        items.forEach((item) => {
          if (typeof item === "string") {
            chunks.push(item.replace(/<[^>]+>/g, " "));
            return;
          }
          if (item && typeof item === "object") {
            if (typeof item.content === "string") chunks.push(item.content.replace(/<[^>]+>/g, " "));
            if (Array.isArray(item.items)) walk(item.items);
          }
        });
      };

      walk(data.items);
    }
  });

  return chunks.join(" ").replace(/\s+/g, " ").trim();
}

function wordsCount(text = "") {
  return String(text || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean).length;
}

function escapeHtml(value = "") {
  return String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function nodeText(node) {
  return String(node?.textContent || "").replace(/\s+/g, " ").trim();
}

function normalizeNativeEditorHtml(html = "") {
  let source = String(html || "").trim();
  if (!source) return "";

  source = source
    .replace(/^(?:\s*<(?:div|p)>\s*<br\s*\/?>\s*<\/(?:div|p)>\s*)+/gi, "")
    .replace(/(?:\s*<(?:div|p)>\s*<br\s*\/?>\s*<\/(?:div|p)>\s*)+$/gi, "")
    .replace(/<br\s*\/?>(\s*)$/gi, "")
    .trim();

  if (!source || /^<(?:div|p)>\s*<br\s*\/?>\s*<\/(?:div|p)>$/i.test(source) || source === "<br>") {
    return "";
  }
  return source;
}

function nativeEditorHtmlToData(html = "") {
  const source = normalizeNativeEditorHtml(html);
  if (!source) return { time: Date.now(), blocks: [] };

  if (typeof document === "undefined") {
    const text = source.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
    return text ? { time: Date.now(), blocks: [{ type: "paragraph", data: { text: escapeHtml(text) } }] } : { time: Date.now(), blocks: [] };
  }

  const template = document.createElement("template");
  template.innerHTML = source;
  const blocks = [];

  const pushParagraph = (value) => {
    const clean = String(value || "").trim();
    if (clean) blocks.push({ type: "paragraph", data: { text: clean } });
  };

  Array.from(template.content.childNodes).forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = nodeText(node);
      if (text) pushParagraph(escapeHtml(text));
      return;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) return;

    const element = node;
    const tag = element.tagName.toLowerCase();

    if (tag === "h2" || tag === "h3") {
      const text = element.innerHTML.trim() || escapeHtml(nodeText(element));
      if (text) blocks.push({ type: "header", data: { text, level: tag === "h2" ? 2 : 3 } });
      return;
    }

    if (tag === "ul" || tag === "ol") {
      const items = Array.from(element.querySelectorAll(":scope > li"))
        .map((li) => li.innerHTML.trim() || escapeHtml(nodeText(li)))
        .filter(Boolean);
      if (items.length) blocks.push({ type: "list", data: { style: tag === "ol" ? "ordered" : "unordered", items } });
      return;
    }

    if (tag === "blockquote") {
      const text = element.innerHTML.trim() || escapeHtml(nodeText(element));
      if (text) blocks.push({ type: "quote", data: { text, caption: "" } });
      return;
    }

    if (tag === "hr") {
      blocks.push({ type: "delimiter", data: {} });
      return;
    }

    if (tag === "p" || tag === "div") {
      const htmlValue = element.innerHTML.trim();
      const textValue = nodeText(element);
      if (htmlValue && textValue) pushParagraph(htmlValue);
      return;
    }

    const textValue = nodeText(element);
    if (textValue) pushParagraph(element.innerHTML.trim() || escapeHtml(textValue));
  });

  return { time: Date.now(), blocks };
}

function nativeBlockHtml(kind) {
  if (kind === "heading2") return "<h2>Новый заголовок</h2>";
  if (kind === "heading3") return "<h3>Новый подзаголовок</h3>";
  if (kind === "quote") return "<blockquote>Цитата</blockquote>";
  if (kind === "list") return "<ul><li>Новый пункт</li></ul>";
  if (kind === "divider") return "<hr>";
  if (kind === "quote-card") return createInlineSpecialBlockHtml("quote");
  if (kind === "highlights") return createInlineSpecialBlockHtml("highlights");
  if (kind === "practice") return createInlineSpecialBlockHtml("practice");
  if (kind === "therapist-note") return createInlineSpecialBlockHtml("note");
  return "<p><br></p>";
}


function createSpecialBlock(kind) {
  const id = `${kind}-${Date.now()}-${Math.random().toString(16).slice(2)}`;

  if (kind === "quote") {
    return {
      id,
      kind,
      text: "Тревога часто говорит голосом срочности. Наша задача — вернуть ей масштаб и опору на реальность.",
      signature: "Психологическая заметка",
    };
  }

  if (kind === "highlights") {
    return {
      id,
      kind,
      title: "Важно запомнить",
      items: [
        "Тревога — это сигнал, а не прогноз будущего.",
        "Мысль можно записать и проверить, не споря с ней в голове.",
        "Маленькое действие возвращает ощущение контроля лучше, чем прокручивание сценариев.",
      ],
    };
  }

  if (kind === "practice") {
    return {
      id,
      kind,
      kicker: "Мягкая практика",
      title: "Упражнение на 3 минуты",
      description: "Подходит для момента, когда мыслей много, а ясности мало. Выполните шаги письменно или в заметках.",
      steps: [
        { title: "Запишите мысль", text: "Одной фразой: «Я тревожусь, что…» без анализа и доказательств." },
        { title: "Назначьте время", text: "Например, 19:00–19:15. До этого момента мысль только хранится." },
        { title: "Вернитесь к делу", text: "Выберите одно маленькое действие, которое можно сделать сейчас." },
      ],
    };
  }

  return {
    id,
    kind: "note",
    title: "Заметка терапевта",
    text: "Если тревога мешает спать, есть панические приступы или избегание важных дел — лучше обсудить это со специалистом.",
  };
}

function specialBlocksToHtml(blocks = []) {
  if (!Array.isArray(blocks) || !blocks.length) return "";

  return blocks
    .map((block) => {
      const kind = String(block?.kind || "").toLowerCase();

      if (kind === "quote") {
        const text = escapeHtml(block?.text).trim();
        const signature = escapeHtml(block?.signature).trim();
        if (!text) return "";
        return `
<div class="art-quote">
  <div class="art-quote-mark">“</div>
  <div class="art-quote-txt">${text}</div>
  ${signature ? `<div class="art-quote-sig">${signature}</div>` : ""}
</div>`;
      }

      if (kind === "highlights") {
        const title = escapeHtml(block?.title || "Важно запомнить").trim();
        const items = (Array.isArray(block?.items) ? block.items : [])
          .map((item) => escapeHtml(item).trim())
          .filter(Boolean)
          .map((item) => `<div class="art-hl-item"><span class="art-hl-dot"></span><span>${item}</span></div>`)
          .join("\n");
        if (!items) return "";
        return `
<div class="art-hl">
  <div class="art-hl-ttl">${title}</div>
  <div class="art-hl-list">${items}</div>
</div>`;
      }

      if (kind === "practice") {
        const kicker = escapeHtml(block?.kicker || "Мягкая практика").trim();
        const title = escapeHtml(block?.title || "Практика").trim();
        const description = escapeHtml(block?.description).trim();
        const steps = (Array.isArray(block?.steps) ? block.steps : [])
          .map((step, index) => {
            const stepTitle = escapeHtml(step?.title).trim();
            const stepText = escapeHtml(step?.text).trim();
            if (!stepTitle && !stepText) return "";
            return `<div class="practice-step"><div class="practice-num">${index + 1}</div>${stepTitle ? `<b>${stepTitle}</b>` : ""}${stepText ? `<span>${stepText}</span>` : ""}</div>`;
          })
          .filter(Boolean)
          .join("\n");
        if (!title && !steps) return "";
        return `
<div class="practice-card">
  <div class="practice-inner">
    ${kicker ? `<div class="practice-kicker">${kicker}</div>` : ""}
    ${title ? `<div class="practice-title">${title}</div>` : ""}
    ${description ? `<div class="practice-desc">${description}</div>` : ""}
    ${steps ? `<div class="practice-steps">${steps}</div>` : ""}
  </div>
</div>`;
      }

      if (kind === "note") {
        const title = escapeHtml(block?.title || "Заметка терапевта").trim();
        const text = escapeHtml(block?.text).trim();
        if (!text) return "";
        return `
<div class="therapist-note">
  <div class="therapist-note-ico">🧠</div>
  <div>${title ? `<b>${title}</b>` : ""}<span>${text}</span></div>
</div>`;
      }

      return "";
    })
    .filter(Boolean)
    .join("\n");
}

function createInlineSpecialBlockHtml(kind) {
  const html = specialBlocksToHtml([createSpecialBlock(kind)]).trim();
  if (!html) return "<p><br></p>";
  return `${html}<p><br></p>`;
}

function getExplicitStatus(post = {}) {
  return String(post?.status || post?.publicationStatus || post?.state || post?.postStatus || "")
    .trim()
    .toUpperCase();
}

function normalizeTagValues(value) {
  const toTag = (item) => {
    if (typeof item === "string") return item.trim();
    if (item && typeof item === "object") {
      return String(item.label || item.name || item.title || item.value || "").trim();
    }
    return "";
  };

  const unique = new Set();
  const add = (item) => {
    const tag = toTag(item);
    if (!tag) return;
    unique.add(tag.replace(/^#/, "").trim());
  };

  if (Array.isArray(value)) {
    value.forEach(add);
  } else if (typeof value === "string") {
    const raw = value.trim();
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) parsed.forEach(add);
        else add(parsed);
      } catch {
        raw
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean)
          .forEach(add);
      }
    }
  } else if (value && typeof value === "object") {
    if (Array.isArray(value.items)) value.items.forEach(add);
    else if (Array.isArray(value.values)) value.values.forEach(add);
    else add(value);
  }

  return Array.from(unique).filter(Boolean).slice(0, 20);
}

function resolveCoverValue(post = {}) {
  return String(
    post?.previewImageUrl ||
      post?.coverImg ||
      post?.coverUrl ||
      post?.coverImageUrl ||
      post?.imageUrl ||
      ""
  ).trim();
}

function resolveEditorContent(post = {}) {
  const directHtml = String(
    post?.content ||
      post?.contentHtml ||
      post?.body ||
      post?.html ||
      ""
  ).trim();
  if (directHtml) return directHtml;

  const rawEditor = post?.editorJson ?? post?.contentJson ?? null;
  if (rawEditor && typeof rawEditor === "object") {
    const htmlFallback = String(rawEditor?.html || rawEditor?.contentHtml || rawEditor?.content || "").trim();
    if (htmlFallback) return htmlFallback;
    return editorDataToHtml(rawEditor);
  }

  const editorText = String(rawEditor || "").trim();
  if (!editorText) return "";
  if (editorText.startsWith("{") || editorText.startsWith("[")) {
    try {
      const parsed = JSON.parse(editorText);
      const htmlFallback = String(parsed?.html || parsed?.contentHtml || parsed?.content || "").trim();
      if (htmlFallback) return htmlFallback;
      return editorDataToHtml(parsed);
    } catch {
      return editorText;
    }
  }
  return editorText;
}

function specialBlocksToText(blocks = []) {
  if (!Array.isArray(blocks) || !blocks.length) return "";
  return blocks
    .flatMap((block) => {
      if (block?.kind === "quote") return [block.text, block.signature];
      if (block?.kind === "highlights") return [block.title, ...(Array.isArray(block.items) ? block.items : [])];
      if (block?.kind === "practice") {
        return [
          block.kicker,
          block.title,
          block.description,
          ...(Array.isArray(block.steps) ? block.steps.flatMap((step) => [step?.title, step?.text]) : []),
        ];
      }
      if (block?.kind === "note") return [block.title, block.text];
      return [];
    })
    .filter(Boolean)
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();
}

function editorDataToHtml(editorData) {
  if (!editorData || !Array.isArray(editorData.blocks) || !editorData.blocks.length) return "";

  const parts = [];

  editorData.blocks.forEach((block) => {
    const type = String(block?.type || "").toLowerCase();
    const data = block?.data || {};

    if (type === "paragraph") {
      const text = escapeHtml(String(data.text || "").trim());
      if (text) parts.push(`<p>${text}</p>`);
      return;
    }

    if (type === "header") {
      const level = Number(data.level) || 2;
      const safeLevel = Math.min(6, Math.max(2, level));
      const text = escapeHtml(String(data.text || "").trim());
      if (text) parts.push(`<h${safeLevel}>${text}</h${safeLevel}>`);
      return;
    }

    if (type === "quote") {
      const text = escapeHtml(String(data.text || "").trim());
      if (text) parts.push(`<blockquote><p>${text}</p></blockquote>`);
      return;
    }

    if (type === "delimiter") {
      parts.push("<hr />");
      return;
    }

    if (type === "list") {
      const style = data.style === "ordered" ? "ol" : "ul";
      const renderItems = (items = []) => {
        return items
          .map((item) => {
            if (typeof item === "string") {
              return `<li>${escapeHtml(item)}</li>`;
            }
            if (item && typeof item === "object") {
              const content = escapeHtml(String(item.content || "").trim());
              const nested = Array.isArray(item.items) && item.items.length ? `<${style}>${renderItems(item.items)}</${style}>` : "";
              if (!content && !nested) return "";
              return `<li>${content}${nested}</li>`;
            }
            return "";
          })
          .join("");
      };

      const itemsHtml = renderItems(data.items || []);
      if (itemsHtml) parts.push(`<${style}>${itemsHtml}</${style}>`);
    }
  });

  return parts.join("\n");
}

function normalizeError(error, fallback) {
  if (!error) return fallback;
  if (typeof error?.message === "string" && error.message.trim()) return error.message;
  if (typeof error?.error === "string" && error.error.trim()) return error.error;
  return fallback;
}

function buildPostPath(post) {
  const id = post?.id;
  const postSlug = String(post?.slug || "").trim();
  if (id && postSlug) return `/blog/${id}-${postSlug}`;
  if (postSlug) return `/blog/${postSlug}`;
  return "/blog";
}

export default function BlogPostEditor() {
  const { me, role } = useAuth();
  const { ensureWithinLimit, checkCreateAccess, limitModal } = useCreateSectionGate();
  const { postId } = useParams();
  const editingPostId = String(postId || "").trim();
  const isEditMode = Boolean(editingPostId);

  const editorHolderRef = React.useRef(null);
  const nativeEditorRef = React.useRef(null);
  const nativeEditorDirtyRef = React.useRef(false);
  const editorReadyRef = React.useRef(false);
  const editorRef = React.useRef(null);
  const saveTimerRef = React.useRef(null);
  const toastTimerRef = React.useRef(null);
  const coverInputRef = React.useRef(null);
  const titleRef = React.useRef(null);
  const excerptRef = React.useRef(null);

  const [saveState, setSaveState] = React.useState("draft");

  const [title, setTitle] = React.useState("");
  const [excerpt, setExcerpt] = React.useState("");

  const [editorData, setEditorData] = React.useState({ blocks: [] });
  const [nativeEditorActive, setNativeEditorActive] = React.useState(true);
  const [editorReady, setEditorReady] = React.useState(false);
  const [specialBlocks, setSpecialBlocks] = React.useState([]);

  const [coverFile, setCoverFile] = React.useState(null);
  const [coverPreview, setCoverPreview] = React.useState("");
  const [coverUrl, setCoverUrl] = React.useState("");

  const [pubMode, setPubMode] = React.useState("Now");
  const [scheduleDt, setScheduleDt] = React.useState(defaultSchedule());

  const [category, setCategory] = React.useState("");
  const [type, setType] = React.useState("Статья");

  const [tags, setTags] = React.useState([]);
  const [tagInput, setTagInput] = React.useState("");

  const [seoTitle, setSeoTitle] = React.useState("");
  const [seoDesc, setSeoDesc] = React.useState("");
  const [slug, setSlug] = React.useState("");
  const [seoTitleManual, setSeoTitleManual] = React.useState(false);
  const [slugManual, setSlugManual] = React.useState(false);

  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const [blockPickerOpen, setBlockPickerOpen] = React.useState(false);
  const [commentsEnabled, setCommentsEnabled] = React.useState(true);
  const [showAuthorEnabled, setShowAuthorEnabled] = React.useState(true);
  const [tocEnabled, setTocEnabled] = React.useState(false);
  const [previewOpen, setPreviewOpen] = React.useState(false);
  const [previewDevice, setPreviewDevice] = React.useState("desk");

  const [submitting, setSubmitting] = React.useState(false);
  const [loadingExistingPost, setLoadingExistingPost] = React.useState(false);
  const [loadedPost, setLoadedPost] = React.useState(null);

  const [toastText, setToastText] = React.useState("");
  const [toastShow, setToastShow] = React.useState(false);
  const [entryCheckDone, setEntryCheckDone] = React.useState(false);
  const [entryAllowed, setEntryAllowed] = React.useState(true);

  const roleToken = String(role || "").replace(/^ROLE_/i, "").toUpperCase();
  const canManage = roleToken === "PSYCHOLOGIST" || roleToken === "ADMIN" || roleToken === "MODERATOR";
  const backPath = roleToken === "MODERATOR" ? "/blog" : "/dashboard/blog";

  const authorName = String(me?.name || "Пользователь").trim();
  const authorInitials = React.useMemo(() => {
    const parts = authorName.split(/\s+/).filter(Boolean);
    if (!parts.length) return "BS";
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
  }, [authorName]);

  const editorPlainText = React.useMemo(() => {
    return [extractTextFromBlocks(editorData), specialBlocksToText(specialBlocks)].filter(Boolean).join(" ");
  }, [editorData, specialBlocks]);
  const wordCnt = React.useMemo(() => wordsCount(editorPlainText), [editorPlainText]);
  const readMinutes = React.useMemo(() => Math.max(1, Math.round(wordCnt / 200)), [wordCnt]);

  const seoCheckTitlePass = title.trim().length > 10;
  const seoCheckWordsPass = wordCnt > 300;
  const seoCheckWordsWarn = wordCnt > 100;
  const seoCheckDescPass = excerpt.trim().length > 40;
  const seoCheckCoverPass = Boolean(coverPreview || coverUrl);
  const seoCheckCategoryPass = Boolean(category);

  const seoScore = React.useMemo(() => {
    let score = 0;
    if (seoCheckTitlePass) score += 20;
    if (seoCheckWordsPass) score += 30;
    else if (seoCheckWordsWarn) score += 15;
    if (seoCheckDescPass) score += 20;
    if (seoCheckCoverPass) score += 15;
    if (seoCheckCategoryPass) score += 15;
    return score;
  }, [
    seoCheckTitlePass,
    seoCheckWordsPass,
    seoCheckWordsWarn,
    seoCheckDescPass,
    seoCheckCoverPass,
    seoCheckCategoryPass,
  ]);

  const seoText = React.useMemo(() => {
    if (seoScore < 21) return ["Начните заполнять", "Заголовок, текст, обложка"];
    if (seoScore < 42) return ["Нужно доработать", "Добавьте больше контента"];
    if (seoScore < 63) return ["Почти готово", "Выберите категорию"];
    if (seoScore < 84) return ["Хорошо!", "Добавьте обложку"];
    return ["Отлично!", "Статья оптимизирована"];
  }, [seoScore]);

  const notifyToast = React.useCallback((message) => {
    setToastText(message);
    setToastShow(true);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToastShow(false), 2400);
  }, []);

  const markEdited = React.useCallback(() => {
    setSaveState("saving");
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => setSaveState("saved"), 1200);
  }, []);

  const syncNativeEditorData = React.useCallback(() => {
    const node = nativeEditorRef.current;
    const html = normalizeNativeEditorHtml(node?.innerHTML || "");
    const data = nativeEditorHtmlToData(html);
    setEditorData(data);
    return data;
  }, []);

  const focusNativeEditor = React.useCallback(() => {
    const node = nativeEditorRef.current;
    if (!node) return;
    node.focus();
  }, []);

  const onNativeEditorInput = React.useCallback(() => {
    nativeEditorDirtyRef.current = true;
    syncNativeEditorData();
    markEdited();
  }, [markEdited, syncNativeEditorData]);

  const onNativeEditorPaste = React.useCallback(
    (event) => {
      event.preventDefault();
      const text = event.clipboardData?.getData("text/plain") || "";
      const html = text
        .split(/\n{2,}/)
        .map((chunk) => chunk.trim())
        .filter(Boolean)
        .map((chunk) => `<p>${escapeHtml(chunk).replace(/\n/g, "<br>")}</p>`)
        .join("");
      if (html && document.queryCommandSupported?.("insertHTML")) {
        document.execCommand("insertHTML", false, html);
      } else if (text && document.queryCommandSupported?.("insertText")) {
        document.execCommand("insertText", false, text);
      }
      window.setTimeout(onNativeEditorInput, 0);
    },
    [onNativeEditorInput]
  );

  const insertNativeEditorBlock = React.useCallback(
    (kind) => {
      const node = nativeEditorRef.current;
      if (!node) return;
      const html = nativeBlockHtml(kind);
      node.focus();

      const selection = window.getSelection?.();
      const range = selection && selection.rangeCount ? selection.getRangeAt(0) : null;
      const selectionInsideEditor = range ? node.contains(range.commonAncestorContainer) : false;

      if (selectionInsideEditor && document.queryCommandSupported?.("insertHTML")) {
        document.execCommand("insertHTML", false, html);
      } else {
        node.insertAdjacentHTML("beforeend", html);
      }

      nativeEditorDirtyRef.current = true;
      syncNativeEditorData();
      markEdited();
    },
    [markEdited, syncNativeEditorData]
  );

  const autoHeight = React.useCallback((el) => {
    if (!el) return;
    const node = el;
    node.style.height = "auto";
    node.style.height = `${node.scrollHeight}px`;
  }, []);

  React.useEffect(() => {
    autoHeight(titleRef.current);
  }, [title, autoHeight]);

  React.useEffect(() => {
    autoHeight(excerptRef.current);
  }, [excerpt, autoHeight]);

  React.useEffect(() => {
    if (seoTitleManual) return;
    setSeoTitle(String(title || "").slice(0, 70));
  }, [title, seoTitleManual]);

  React.useEffect(() => {
    if (slugManual) return;
    setSlug(slugify(title));
  }, [title, slugManual]);

  React.useEffect(() => {
    let alive = true;
    if (!canManage) {
      setEntryAllowed(false);
      setEntryCheckDone(true);
      return undefined;
    }

    if (isEditMode) {
      setEntryAllowed(true);
      setEntryCheckDone(true);
      return () => {
        alive = false;
      };
    }

    (async () => {
      const allowed = await checkCreateAccess("/dashboard/blog/new");
      if (!alive) return;
      setEntryAllowed(Boolean(allowed));
      setEntryCheckDone(true);
    })();

    return () => {
      alive = false;
    };
  }, [canManage, checkCreateAccess, isEditMode]);

  React.useEffect(() => {
    const onClickOutside = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest(".add-block-wrap")) return;
      setBlockPickerOpen(false);
    };

    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  React.useEffect(() => {
    if (!previewOpen) {
      document.body.style.overflow = "";
      return;
    }
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [previewOpen]);

  React.useEffect(() => {
    if (!isEditMode || !entryCheckDone || !entryAllowed) return undefined;

    let alive = true;
    setLoadingExistingPost(true);

    (async () => {
      try {
        const post = await blogApi.getMineById(editingPostId);
        if (!alive) return;

        const normalized = post && typeof post === "object" ? post : null;
        setLoadedPost(normalized);
        if (!normalized) {
          notifyToast("Статья не найдена");
          return;
        }

        setTitle(String(normalized.title || "").slice(0, BLOG_TITLE_MAX));
        setExcerpt(String(normalized.excerpt || normalized.description || "").slice(0, BLOG_EXCERPT_MAX));
        setCategory(String(normalized.category || normalized.categoryTitle || ""));
        setType(String(normalized.materialType || normalized.type || "Статья"));
        setTags(normalizeTagValues(normalized.tags));
        setCoverUrl(resolveCoverValue(normalized));
        setCoverFile(null);
        setCoverPreview("");

        const explicitStatus = getExplicitStatus(normalized);
        setPubMode(explicitStatus === "DRAFT" ? "Draft" : explicitStatus === "SCHEDULED" ? "Sched" : "Now");
        if (normalized.scheduledAt || normalized.publishAt || normalized.publishedAt) {
          setScheduleDt(toLocalDatetimeValue(normalized.scheduledAt || normalized.publishAt || normalized.publishedAt));
        }

        const seo = normalized.seo && typeof normalized.seo === "object" ? normalized.seo : {};
        setSeoTitle(String(normalized.seoTitle || seo.title || normalized.title || "").slice(0, SEO_TITLE_MAX));
        setSeoDesc(String(normalized.seoDescription || seo.description || normalized.description || "").slice(0, SEO_DESC_MAX));
        setSlug(normalizeSeoSlug(normalized.slug || seo.slug || seo.urlSlug || ""));
        setSeoTitleManual(Boolean(normalized.seoTitle || seo.title));
        setSlugManual(Boolean(normalized.slug || seo.slug));
        setCommentsEnabled(normalized.commentsEnabled !== false);
        setShowAuthorEnabled(normalized.showAuthor !== false);
        setTocEnabled(Boolean(normalized.tocEnabled));
        setSpecialBlocks([]);

        const html = resolveEditorContent(normalized);
        if (nativeEditorRef.current) {
          nativeEditorRef.current.innerHTML = html;
          nativeEditorDirtyRef.current = true;
          syncNativeEditorData();
        }
        setSaveState("saved");
      } catch (error) {
        if (alive) notifyToast(normalizeError(error, "Не удалось загрузить статью"));
      } finally {
        if (alive) setLoadingExistingPost(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [editingPostId, entryAllowed, entryCheckDone, isEditMode, notifyToast, syncNativeEditorData]);

  React.useEffect(() => {
    // Единый режим редактора: блоки добавляются прямо в общий поток статьи.
    // Не переключаемся на EditorJS, чтобы порядок текста, цитат и спец-блоков
    // всегда совпадал с порядком добавления и сохранения.
    editorReadyRef.current = false;
    setEditorReady(false);
    setNativeEditorActive(true);
    return () => {
      if (editorRef.current && typeof editorRef.current.destroy === "function") {
        editorRef.current.destroy();
      }
      editorRef.current = null;
      editorReadyRef.current = false;
    };
  }, []);

  React.useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
      if (coverPreview && coverPreview.startsWith("blob:")) URL.revokeObjectURL(coverPreview);
    };
  }, [coverPreview]);

  const onCoverSelected = React.useCallback(
    (file) => {
      if (!file) return;
      if (coverPreview && coverPreview.startsWith("blob:")) URL.revokeObjectURL(coverPreview);

      const blobUrl = URL.createObjectURL(file);
      setCoverFile(file);
      setCoverUrl("");
      setCoverPreview(blobUrl);
      markEdited();
    },
    [coverPreview, markEdited]
  );

  const removeCover = React.useCallback(
    (event) => {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      if (coverPreview && coverPreview.startsWith("blob:")) URL.revokeObjectURL(coverPreview);
      setCoverFile(null);
      setCoverUrl("");
      setCoverPreview("");
      if (coverInputRef.current) coverInputRef.current.value = "";
      markEdited();
    },
    [coverPreview, markEdited]
  );

  const addTag = React.useCallback(
    (value) => {
      const normalized = String(value || "")
        .trim()
        .replace(/^#/, "");
      if (!normalized) return;

      setTags((prev) => {
        if (prev.some((tag) => tag.toLowerCase() === normalized.toLowerCase())) return prev;
        return [...prev, normalized].slice(0, 20);
      });
      setTagInput("");
      markEdited();
    },
    [markEdited]
  );

  const removeTag = React.useCallback(
    (idx) => {
      setTags((prev) => prev.filter((_, index) => index !== idx));
      markEdited();
    },
    [markEdited]
  );

  const openCoverPicker = React.useCallback(() => {
    if (!coverInputRef.current) return;
    // Allow selecting the same file again and keep picker open behavior consistent across browsers.
    coverInputRef.current.value = "";
    coverInputRef.current.click();
  }, []);

  const updateSpecialBlock = React.useCallback(
    (id, patch) => {
      setSpecialBlocks((prev) => prev.map((block) => (block.id === id ? { ...block, ...patch } : block)));
      markEdited();
    },
    [markEdited]
  );

  const updateSpecialBlockItem = React.useCallback(
    (id, key, index, value) => {
      setSpecialBlocks((prev) =>
        prev.map((block) => {
          if (block.id !== id) return block;
          const list = Array.isArray(block[key]) ? [...block[key]] : [];
          if (typeof list[index] === "object" && list[index] !== null) {
            list[index] = { ...list[index], ...value };
          } else {
            list[index] = value;
          }
          return { ...block, [key]: list };
        })
      );
      markEdited();
    },
    [markEdited]
  );

  const removeSpecialBlock = React.useCallback(
    (id) => {
      setSpecialBlocks((prev) => prev.filter((block) => block.id !== id));
      markEdited();
      notifyToast("Блок удалён");
    },
    [markEdited, notifyToast]
  );

  const readCurrentEditorData = React.useCallback(async () => {
    if (nativeEditorActive || nativeEditorDirtyRef.current || !editorRef.current || typeof editorRef.current.save !== "function") {
      return syncNativeEditorData();
    }

    try {
      const saved = await editorRef.current.save();
      setEditorData(saved || { blocks: [] });
      return saved || { blocks: [] };
    } catch {
      return syncNativeEditorData();
    }
  }, [nativeEditorActive, syncNativeEditorData]);

  const insertEditorBlock = React.useCallback(
    async (kind) => {
      if (kind === "image") {
        openCoverPicker();
        setBlockPickerOpen(false);
        notifyToast("Изображение загружается как обложка статьи");
        return;
      }

      if (["quote-card", "highlights", "practice", "therapist-note"].includes(kind)) {
        setNativeEditorActive(true);
        insertNativeEditorBlock(kind);
        setBlockPickerOpen(false);
        notifyToast("Блок добавлен в статью");
        return;
      }

      if (nativeEditorActive || nativeEditorDirtyRef.current || !editorRef.current?.blocks) {
        setNativeEditorActive(true);
        insertNativeEditorBlock(kind);
        setBlockPickerOpen(false);
        return;
      }

      let typeName = "paragraph";
      let data = { text: "" };

      if (kind === "heading2") {
        typeName = "header";
        data = { text: "Новый заголовок", level: 2 };
      } else if (kind === "heading3") {
        typeName = "header";
        data = { text: "Новый подзаголовок", level: 3 };
      } else if (kind === "quote") {
        typeName = "quote";
        data = { text: "Цитата", caption: "" };
      } else if (kind === "list") {
        typeName = "list";
        data = { style: "unordered", items: ["Новый пункт"] };
      } else if (kind === "divider") {
        typeName = "delimiter";
        data = {};
      }

      try {
        await editorRef.current.blocks.insert(typeName, data, undefined, undefined, true);
        await readCurrentEditorData();
        markEdited();
      } catch {
        notifyToast("Не удалось добавить блок");
      } finally {
        setBlockPickerOpen(false);
      }
    },
    [insertNativeEditorBlock, markEdited, nativeEditorActive, notifyToast, openCoverPicker, readCurrentEditorData]
  );

  const uploadCoverIfNeeded = React.useCallback(async () => {
    if (!coverFile) return coverUrl || "";

    const uploaded = await filesApi.uploadImage(coverFile);
    const url =
      uploaded?.file?.url ||
      uploaded?.url ||
      uploaded?.item?.url ||
      uploaded?.item?.file?.url ||
      uploaded?.data?.url ||
      "";

    if (!url) throw new Error("Не удалось загрузить обложку");
    setCoverUrl(url);
    return url;
  }, [coverFile, coverUrl]);

  const publish = React.useCallback(
    async (mode = "auto") => {
        const titleValue = String(title || "").trim().slice(0, BLOG_TITLE_MAX);
      if (!titleValue) {
        notifyToast("⚠️ Введите заголовок");
        titleRef.current?.focus();
        return;
      }

      if (!category) {
        notifyToast("⚠️ Выберите категорию");
        return;
      }

      if (!isEditMode) {
        try {
          const mine = await blogApi.listMine({ page: 0, size: 1 });
          const usage = Number(mine?.totalElements) || 0;
          const allowed = ensureWithinLimit({ featureKey: "blogPosts", usage });
          if (!allowed) return;
        } catch (limitError) {
          notifyToast(normalizeError(limitError, "Не удалось проверить лимит подписки"));
          return;
        }
      }

      setSubmitting(true);
      setSaveState("saving");

      try {
        const data = await readCurrentEditorData();
        const cover = await uploadCoverIfNeeded();

        const effectiveMode = mode === "auto" ? pubMode : mode;
        const status =
          effectiveMode === "Draft"
            ? "DRAFT"
            : effectiveMode === "Sched"
              ? "SCHEDULED"
              : "PUBLISHED";

        const publishAt =
          status === "SCHEDULED" ? toMoscowIsoDateTime(scheduleDt) : null;

        if (status === "SCHEDULED" && !publishAt) {
          notifyToast("⚠️ Укажите дату и время публикации по МСК");
          setSaveState("draft");
          setSubmitting(false);
          return;
        }

        const slugValue = normalizeSeoSlug(slug.trim()) || slugify(titleValue);
        const seoTitleValue = String(seoTitle || titleValue).trim().slice(0, SEO_TITLE_MAX);
        const seoDescValue = String(seoDesc || "").trim().slice(0, SEO_DESC_MAX);
        const nativeHtml = normalizeNativeEditorHtml(nativeEditorRef.current?.innerHTML || "");
        const baseContentHtml = nativeHtml || editorDataToHtml(data);
        const normalizedEditorData = data && typeof data === "object" ? data : nativeEditorHtmlToData(baseContentHtml);
        const contentHtml = [baseContentHtml, specialBlocksToHtml(specialBlocks)].filter(Boolean).join("\n");

        const payload = {
          title: titleValue,
          description: String(excerpt || "").trim().slice(0, BLOG_EXCERPT_MAX),
          excerpt: String(excerpt || "").trim().slice(0, BLOG_EXCERPT_MAX),
          content: contentHtml,
          contentHtml,
          editorJson: {
            format: "html",
            html: contentHtml,
            blocks: Array.isArray(normalizedEditorData?.blocks) ? normalizedEditorData.blocks : [],
            time: Number(normalizedEditorData?.time) || Date.now(),
          },
          category,
          categoryTitle: category,
          type,
          materialType: type,
          tags: tags.filter(Boolean),
          previewImageUrl: cover || null,
          coverImg: cover || null,
          status,
          publishAt,
          scheduledAt: publishAt,
          commentsEnabled,
          showAuthor: showAuthorEnabled,
          tocEnabled,
          slug: slugValue,
          seoTitle: seoTitleValue,
          seoDescription: seoDescValue,
          seo: {
            title: seoTitleValue,
            description: seoDescValue,
            slug: slugValue,
          },
        };

        const savedPost = isEditMode
          ? await blogApi.updateForRole(editingPostId, payload)
          : await blogApi.createForRole(payload);

        if (status === "PUBLISHED") {
          setSaveState("published");
          notifyToast(isEditMode ? "🚀 Статья обновлена и опубликована" : "🚀 Статья опубликована");
          const nextPath = buildPostPath(savedPost);
          if (nextPath !== "/blog") {
            setTimeout(() => {
              window.location.assign(nextPath);
            }, 280);
          }
          return;
        }

        setSaveState("saved");
        notifyToast(status === "SCHEDULED" ? "🕐 Публикация запланирована" : "💾 Черновик сохранён");
        if (isEditMode) setLoadedPost(savedPost && typeof savedPost === "object" ? savedPost : loadedPost);
      } catch (error) {
        setSaveState("draft");
        notifyToast(normalizeError(error, "Не удалось сохранить статью"));
      } finally {
        setSubmitting(false);
      }
    },
    [
      title,
      category,
      excerpt,
      type,
      tags,
      pubMode,
      scheduleDt,
      slug,
      seoTitle,
      seoDesc,
      specialBlocks,
      commentsEnabled,
      showAuthorEnabled,
      tocEnabled,
      readCurrentEditorData,
      uploadCoverIfNeeded,
      notifyToast,
      ensureWithinLimit,
      isEditMode,
      editingPostId,
      loadedPost,
    ]
  );

  const clearDraft = React.useCallback(() => {
    if (!window.confirm(isEditMode ? "Очистить поля редактора? Статья на сервере не удалится." : "Удалить черновик?")) return;

    setTitle("");
    setExcerpt("");
    setCategory("");
    setType("Статья");
    setTags([]);
    setTagInput("");
    setSpecialBlocks([]);
    setSeoTitle("");
    setSeoDesc("");
    setSeoTitleManual(false);
    setSlug("");
    setSlugManual(false);
    setPubMode("Now");
    setScheduleDt(defaultSchedule());
    removeCover();

    if (editorRef.current && typeof editorRef.current.clear === "function") {
      editorRef.current.clear();
    }
    if (nativeEditorRef.current) nativeEditorRef.current.innerHTML = "";
    nativeEditorDirtyRef.current = false;
    setNativeEditorActive(!editorReadyRef.current);

    setEditorData({ blocks: [] });
    setSaveState("draft");
    notifyToast("Удалено");
  }, [isEditMode, notifyToast, removeCover]);

  const previewTitle = title.trim() || "Без заголовка";
  const previewCategory = category || "";
  const blockCount = (Array.isArray(editorData?.blocks) ? editorData.blocks.length : 0) + specialBlocks.length;
  const seoPreviewTitle = String(seoTitle || title || "Без заголовка").trim();
  const seoPreviewDesc = String(seoDesc || excerpt || "Описание статьи для поисковых систем").trim();
  const previewArticleHtml = React.useMemo(() => {
    const nativeHtml = normalizeNativeEditorHtml(nativeEditorRef.current?.innerHTML || "");
    return [nativeHtml || editorDataToHtml(editorData), specialBlocksToHtml(specialBlocks)].filter(Boolean).join("\n");
  }, [editorData, specialBlocks]);

  const saveText =
    saveState === "saving"
      ? "Сохранение…"
      : saveState === "saved"
        ? "Сохранено"
        : saveState === "published"
          ? "Опубликовано"
          : "Черновик";

  const renderSpecialBlockEditor = React.useCallback(
    (block, index) => {
      const header = {
        quote: ["Цитата", "Выделенный смысловой блок как в детальной статье"],
        highlights: ["Важно запомнить", "Короткие тезисы с зелёными маркерами"],
        practice: ["Практика", "Тёмная карточка с пошаговым упражнением"],
        note: ["Заметка терапевта", "Мягкое предупреждение или рекомендация автора"],
      }[block.kind] || ["Блок", "Дополнительный блок статьи"];

      return (
        <div key={block.id} className={`unique-block unique-block--${block.kind}`}>
          <div className="unique-block-head">
            <div>
              <div className="unique-block-kicker">Блок {index + 1}</div>
              <div className="unique-block-title">{header[0]}</div>
              <div className="unique-block-desc">{header[1]}</div>
            </div>
            <button type="button" className="unique-block-remove" onClick={() => removeSpecialBlock(block.id)}>
              Удалить
            </button>
          </div>

          {block.kind === "quote" ? (
            <div className="unique-block-grid">
              <label className="unique-field unique-field--wide">
                <span>Текст цитаты</span>
                <textarea
                  value={block.text || ""}
                  rows={3}
                  onChange={(event) => updateSpecialBlock(block.id, { text: event.target.value })}
                />
              </label>
              <label className="unique-field">
                <span>Подпись</span>
                <input
                  value={block.signature || ""}
                  onChange={(event) => updateSpecialBlock(block.id, { signature: event.target.value })}
                />
              </label>
            </div>
          ) : null}

          {block.kind === "highlights" ? (
            <div className="unique-block-stack">
              <label className="unique-field">
                <span>Заголовок блока</span>
                <input value={block.title || ""} onChange={(event) => updateSpecialBlock(block.id, { title: event.target.value })} />
              </label>
              {(Array.isArray(block.items) ? block.items : []).map((item, itemIndex) => (
                <label key={`${block.id}-item-${itemIndex}`} className="unique-field">
                  <span>Тезис {itemIndex + 1}</span>
                  <textarea
                    value={item || ""}
                    rows={2}
                    onChange={(event) => updateSpecialBlockItem(block.id, "items", itemIndex, event.target.value)}
                  />
                </label>
              ))}
            </div>
          ) : null}

          {block.kind === "practice" ? (
            <div className="unique-block-stack">
              <div className="unique-block-grid">
                <label className="unique-field">
                  <span>Kicker</span>
                  <input value={block.kicker || ""} onChange={(event) => updateSpecialBlock(block.id, { kicker: event.target.value })} />
                </label>
                <label className="unique-field">
                  <span>Заголовок практики</span>
                  <input value={block.title || ""} onChange={(event) => updateSpecialBlock(block.id, { title: event.target.value })} />
                </label>
              </div>
              <label className="unique-field">
                <span>Описание</span>
                <textarea rows={2} value={block.description || ""} onChange={(event) => updateSpecialBlock(block.id, { description: event.target.value })} />
              </label>
              {(Array.isArray(block.steps) ? block.steps : []).map((step, stepIndex) => (
                <div key={`${block.id}-step-${stepIndex}`} className="unique-step-row">
                  <div className="unique-step-num">{stepIndex + 1}</div>
                  <div className="unique-step-fields">
                    <label className="unique-field">
                      <span>Шаг {stepIndex + 1}</span>
                      <input
                        value={step?.title || ""}
                        onChange={(event) => updateSpecialBlockItem(block.id, "steps", stepIndex, { title: event.target.value })}
                      />
                    </label>
                    <label className="unique-field">
                      <span>Описание шага</span>
                      <textarea
                        rows={2}
                        value={step?.text || ""}
                        onChange={(event) => updateSpecialBlockItem(block.id, "steps", stepIndex, { text: event.target.value })}
                      />
                    </label>
                  </div>
                </div>
              ))}
            </div>
          ) : null}

          {block.kind === "note" ? (
            <div className="unique-block-stack">
              <label className="unique-field">
                <span>Заголовок</span>
                <input value={block.title || ""} onChange={(event) => updateSpecialBlock(block.id, { title: event.target.value })} />
              </label>
              <label className="unique-field">
                <span>Текст заметки</span>
                <textarea rows={3} value={block.text || ""} onChange={(event) => updateSpecialBlock(block.id, { text: event.target.value })} />
              </label>
            </div>
          ) : null}
        </div>
      );
    },
    [removeSpecialBlock, updateSpecialBlock, updateSpecialBlockItem]
  );

  if (!canManage) {
    return (
      <section className="dashboard-placeholder" style={{ marginBottom: 24 }}>
        <div className="dashboard-placeholder__hero">
          <h3>Доступ ограничен</h3>
          <p>Управление статьями доступно только психологам, администраторам и модераторам.</p>
        </div>
        <div className="dashboard-placeholder__actions">
          <Link className="dashboard__btn dashboard__btn--ghost" to={backPath}>
            ← Назад
          </Link>
        </div>
      </section>
    );
  }

  if (!entryCheckDone) {
    return <section className="b-blog-editor-mock" />;
  }

  if (!entryAllowed) {
    return (
      <>
        <section className="dashboard-placeholder" style={{ marginBottom: 24 }}>
          <div className="dashboard-placeholder__hero">
            <h3>Лимит достигнут</h3>
            <p>Для создания новых статей перейдите на тариф выше в разделе подписки.</p>
          </div>
          <div className="dashboard-placeholder__actions">
            <Link className="dashboard__btn dashboard__btn--ghost" to={backPath}>
              ← Назад
            </Link>
          </div>
        </section>
        {limitModal}
      </>
    );
  }

  return (
    <section className="b-blog-editor-mock">
      <nav className="editor-nav">
        <div className="nav-left">
          <Link className="nav-back" to={backPath} title="Назад">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M19 12H5M12 19l-7-7 7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </Link>
          <img className="editor-nav__logo" src={brandLogo} alt="Бюро Души" />
          <div className="nav-divider" />
          <span className="nav-page-title">{isEditMode ? "Редактирование статьи" : "Новая статья"}</span>
        </div>

        <div className="nav-center">
          <div className="nav-status">
            <div className={`nav-status-dot ${saveState === "saving" ? "saving" : ""}`} />
            {saveText}
          </div>
        </div>

        <div className="nav-right">
          <button className="btn-ghost" onClick={() => setPreviewOpen(true)}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
            </svg>
            <span className="desktop-label">Предпросмотр</span>
          </button>
          <button className="btn-ghost" onClick={() => publish("Draft")} disabled={submitting || loadingExistingPost}>
            <span className="desktop-label">Черновик</span>
          </button>
          <button className="btn-publish" onClick={() => publish("auto")} disabled={submitting || loadingExistingPost}>
            {submitting ? "Сохранение…" : isEditMode ? "Сохранить" : "Опубликовать"}
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
          <button className="btn-sidebar-toggle" onClick={() => setSidebarOpen((prev) => !prev)}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" />
              <line x1="15" y1="3" x2="15" y2="21" stroke="currentColor" strokeWidth="2" />
            </svg>
          </button>
        </div>
      </nav>

      <div className="editor-layout">
        <main className="editor-main">
          <div className="editor-canvas">
            {loadingExistingPost ? <div className="editor-load-existing">Загружаем статью для редактирования…</div> : null}
            <div
              className={`cover-zone ${coverPreview || coverUrl ? "has-image" : ""}`}
              role="button"
              tabIndex={0}
              aria-label="Выбрать обложку статьи"
              onClick={openCoverPicker}
              onKeyDown={(event) => {
                if (event.key !== "Enter" && event.key !== " ") return;
                event.preventDefault();
                openCoverPicker();
              }}
            >
              <input
                ref={coverInputRef}
                id="coverFile"
                type="file"
                accept="image/*"
                onChange={(event) => onCoverSelected(event.target.files?.[0])}
                className="cover-file-input"
              />
              <div className="cover-placeholder">
                <div className="cover-icon">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.5" />
                    <circle cx="8.5" cy="8.5" r="1.5" stroke="currentColor" strokeWidth="1.5" />
                    <path d="M21 15l-5-5L5 21" stroke="currentColor" strokeWidth="1.5" />
                  </svg>
                </div>
                <div className="cover-label">Загрузить обложку</div>
                <div className="cover-hint">Перетащите изображение или нажмите · 1200×520 рекомендуется</div>
              </div>

              {coverPreview || coverUrl ? <img className="cover-preview" src={coverPreview || coverUrl} alt="Обложка" /> : null}

              {coverPreview || coverUrl ? (
                <div className="cover-actions">
                  <button
                    className="cover-btn"
                    type="button"
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      openCoverPicker();
                    }}
                  >
                    Заменить
                  </button>
                  <button className="cover-btn" type="button" onClick={removeCover}>
                    Удалить
                  </button>
                </div>
              ) : null}
            </div>

            <div className="title-zone">
              <textarea
                ref={titleRef}
                className="title-input"
                rows={1}
                maxLength={BLOG_TITLE_MAX}
                placeholder="Заголовок статьи"
                value={title}
                onChange={(event) => {
                  setTitle(event.target.value);
                  markEdited();
                }}
              />
              <div className="title-field-meta">
                <span>Заголовок</span>
                <span>
                  {String(title || "").length}/{BLOG_TITLE_MAX}
                </span>
              </div>

              <textarea
                ref={excerptRef}
                className="subtitle-input"
                rows={1}
                maxLength={BLOG_EXCERPT_MAX}
                placeholder="Подзаголовок (необязательно)"
                value={excerpt}
                onChange={(event) => {
                  setExcerpt(event.target.value);
                  markEdited();
                }}
              />
              <div className="title-field-meta">
                <span>Подзаголовок</span>
                <span>
                  {String(excerpt || "").length}/{BLOG_EXCERPT_MAX}
                </span>
              </div>
            </div>

            <div className="editor-sep" />

            <div className="floating-toolbar">
              <button className="tb-dropdown" onClick={() => insertEditorBlock("paragraph")}>
                Абзац
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                </svg>
              </button>
              <div className="tb-sep" />
              <button className="tb-btn" onClick={() => insertEditorBlock("heading2")} title="H2">
                H2
              </button>
              <button className="tb-btn" onClick={() => insertEditorBlock("heading3")} title="H3">
                H3
              </button>
              <button className="tb-btn" onClick={() => insertEditorBlock("quote")} title="Цитата">
                “
              </button>
              <button className="tb-btn" onClick={() => insertEditorBlock("list")} title="Список">
                ≡
              </button>
              <button className="tb-btn" onClick={() => insertEditorBlock("divider")} title="Разделитель">
                —
              </button>
              <button className="tb-btn" onClick={() => insertEditorBlock("image")} title="Изображение">
                🖼
              </button>
              <button className="tb-btn tb-btn-wide" onClick={() => insertEditorBlock("quote-card")} title="Цитата из мока">
                Цит
              </button>
              <button className="tb-btn tb-btn-wide" onClick={() => insertEditorBlock("highlights")} title="Важно запомнить">
                Важ
              </button>
              <button className="tb-btn tb-btn-wide" onClick={() => insertEditorBlock("practice")} title="Практика">
                Пра
              </button>
              <button className="tb-btn tb-btn-wide" onClick={() => insertEditorBlock("therapist-note")} title="Заметка терапевта">
                Зам
              </button>
            </div>

            <div className="editor-content">
              <div
                className={`editor-wrap ${nativeEditorActive ? "is-native-active" : "is-editorjs-active"}`}
                onMouseDown={(event) => {
                  if (!nativeEditorActive) return;
                  if (event.target === event.currentTarget) focusNativeEditor();
                }}
                onClick={(event) => {
                  if (!nativeEditorActive) return;
                  if (event.target === event.currentTarget) focusNativeEditor();
                }}
              >
                <div id="editorjs" className={nativeEditorActive ? "is-hidden" : ""} ref={editorHolderRef} />
                {nativeEditorActive ? (
                  <div
                    ref={nativeEditorRef}
                    className="native-article-editor"
                    contentEditable
                    suppressContentEditableWarning
                    role="textbox"
                    aria-label="Текст статьи"
                    data-placeholder="Начните писать статью… Можно нажать + и добавить заголовок, список, цитату или уникальный блок"
                    onInput={onNativeEditorInput}
                    onPaste={onNativeEditorPaste}
                  />
                ) : null}
                {!editorReady && !nativeEditorActive ? <div className="editor-loading-note">Загружаем редактор…</div> : null}
              </div>
            </div>

            {specialBlocks.length ? (
              <div className="unique-blocks-zone">
                <div className="unique-blocks-title">Уникальные блоки детальной статьи</div>
                {specialBlocks.map((block, index) => renderSpecialBlockEditor(block, index))}
              </div>
            ) : null}

            <div className="add-block-wrap">
              <div className="add-block-row" onClick={() => setBlockPickerOpen((prev) => !prev)}>
                <button className="add-block-btn">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <line x1="12" y1="5" x2="12" y2="19" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                    <line x1="5" y1="12" x2="19" y2="12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                  </svg>
                </button>
                <span className="add-block-text">Добавить блок</span>
              </div>

              <div className={`block-picker ${blockPickerOpen ? "open" : ""}`}>
                <div className="bp-item" onClick={() => insertEditorBlock("paragraph")}>
                  <div className="bp-icon grey-bg">T</div>
                  <div className="bp-info">
                    <div className="bp-name">Текст</div>
                    <div className="bp-desc">Обычный абзац</div>
                  </div>
                </div>
                <div className="bp-item" onClick={() => insertEditorBlock("heading2")}>
                  <div className="bp-icon mint-bg">H2</div>
                  <div className="bp-info">
                    <div className="bp-name">Заголовок H2</div>
                    <div className="bp-desc">Крупный подзаголовок</div>
                  </div>
                </div>
                <div className="bp-item" onClick={() => insertEditorBlock("heading3")}>
                  <div className="bp-icon mint-bg">H3</div>
                  <div className="bp-info">
                    <div className="bp-name">Заголовок H3</div>
                    <div className="bp-desc">Малый подзаголовок</div>
                  </div>
                </div>
                <div className="bp-item" onClick={() => insertEditorBlock("quote")}>
                  <div className="bp-icon lav-bg">❞</div>
                  <div className="bp-info">
                    <div className="bp-name">Цитата</div>
                    <div className="bp-desc">Выделенный блок</div>
                  </div>
                </div>
                <div className="bp-item" onClick={() => insertEditorBlock("list")}>
                  <div className="bp-icon grey-bg">•</div>
                  <div className="bp-info">
                    <div className="bp-name">Список</div>
                    <div className="bp-desc">Маркированный список</div>
                  </div>
                </div>
                <div className="bp-item" onClick={() => insertEditorBlock("quote-card")}>
                  <div className="bp-icon lav-bg">“</div>
                  <div className="bp-info">
                    <div className="bp-name">Цитата в карточке</div>
                    <div className="bp-desc">Блок из детальной статьи</div>
                  </div>
                </div>
                <div className="bp-item" onClick={() => insertEditorBlock("highlights")}>
                  <div className="bp-icon mint-bg">✓</div>
                  <div className="bp-info">
                    <div className="bp-name">Важно запомнить</div>
                    <div className="bp-desc">Тезисы с маркерами</div>
                  </div>
                </div>
                <div className="bp-item" onClick={() => insertEditorBlock("practice")}>
                  <div className="bp-icon dark-bg">3</div>
                  <div className="bp-info">
                    <div className="bp-name">Практика</div>
                    <div className="bp-desc">Тёмная карточка с шагами</div>
                  </div>
                </div>
                <div className="bp-item" onClick={() => insertEditorBlock("therapist-note")}>
                  <div className="bp-icon mint-bg">🧠</div>
                  <div className="bp-info">
                    <div className="bp-name">Заметка терапевта</div>
                    <div className="bp-desc">Мягкая авторская ремарка</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="word-bar">
              <span>{wordCnt} слов</span>
              <span>~ {readMinutes} мин чтения</span>
              <span>{blockCount} блоков</span>
            </div>
          </div>
        </main>

        <div className={`sidebar-overlay ${sidebarOpen ? "open" : ""}`} onClick={() => setSidebarOpen(false)} />
        <aside className={`editor-sidebar ${sidebarOpen ? "open" : ""}`}>
          <div className="sb-section">
            <div className="sb-section-title">Публикация</div>
            <div className="sb-field">
              <div className="sb-label">Статус</div>
              <select
                className="sb-select"
                value={pubMode}
                onChange={(event) => {
                  setPubMode(event.target.value);
                  markEdited();
                }}
              >
                <option value="Draft">Черновик</option>
                <option value="Now">Опубликовать сразу</option>
                <option value="Sched">По расписанию</option>
              </select>
            </div>
            {pubMode === "Sched" ? (
              <div className="sb-field">
                <div className="sb-label">Дата публикации</div>
                <input
                  className="sb-input"
                  type="datetime-local"
                  value={scheduleDt}
                  onChange={(event) => {
                    setScheduleDt(event.target.value);
                    markEdited();
                  }}
                />
                <div className="sb-hint">Время публикации указывается по МСК (UTC+3)</div>
              </div>
            ) : null}
          </div>

          <div className="sb-section">
            <div className="sb-section-title">Категория и теги</div>
            <div className="sb-field">
              <div className="sb-label">Категория</div>
              <select
                className="sb-select"
                value={category}
                onChange={(event) => {
                  setCategory(event.target.value);
                  markEdited();
                }}
              >
                <option value="">Выберите категорию</option>
                {CATEGORY_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.value}
                  </option>
                ))}
              </select>
            </div>
            <div className="sb-field">
              <div className="sb-label">Тип материала</div>
              <select
                className="sb-select"
                value={type}
                onChange={(event) => {
                  setType(event.target.value);
                  markEdited();
                }}
              >
                {MATERIAL_TYPES.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.value}
                  </option>
                ))}
              </select>
            </div>
            <div className="sb-field">
              <div className="sb-label">Теги</div>
              <div className="sb-tags">
                {tags.map((tag, idx) => (
                  <div key={`${tag}-${idx}`} className={`sb-tag ${idx % 2 ? "lav" : "mint"}`}>
                    #{tag}
                    <button className="sb-tag-remove" onClick={() => removeTag(idx)}>
                      ×
                    </button>
                  </div>
                ))}
              </div>
              <input
                className="sb-input"
                placeholder="Добавить тег…"
                value={tagInput}
                onChange={(event) => setTagInput(event.target.value)}
                onBlur={() => addTag(tagInput)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === ",") {
                    event.preventDefault();
                    addTag(tagInput);
                  }
                }}
              />
            </div>
          </div>

          <div className="sb-section">
            <div className="sb-section-title">SEO</div>
            <div className="sb-field">
              <div className="sb-label">SEO-заголовок</div>
              <input
                type="text"
                className="sb-input"
                placeholder="Для поисковых систем"
                maxLength={SEO_TITLE_MAX}
                value={seoTitle}
                onChange={(event) => {
                  setSeoTitle(event.target.value);
                  setSeoTitleManual(true);
                  markEdited();
                }}
              />
              <div className="sb-hint sb-hint-row">
                <span>До {SEO_TITLE_MAX} символов</span>
                <span>
                  {String(seoTitle || "").length}/{SEO_TITLE_MAX}
                </span>
              </div>
            </div>
            <div className="sb-field">
              <div className="sb-label">URL-адрес</div>
              <input
                className="sb-input"
                value={slug}
                onChange={(event) => {
                  setSlugManual(true);
                  setSlug(normalizeSeoSlug(event.target.value));
                  markEdited();
                }}
              />
              <div className="sb-hint sb-hint-row">
                <span>Только `a-z`, `0-9` и `-`</span>
                <span>
                  {String(slug || "").length}/{SEO_SLUG_MAX}
                </span>
              </div>
            </div>
            <div className="sb-field">
              <div className="sb-label">Описание для поиска</div>
              <textarea
                className="sb-textarea"
                rows={3}
                maxLength={SEO_DESC_MAX}
                placeholder="Краткое описание для Google / Яндекс…"
                value={seoDesc}
                onChange={(event) => {
                  setSeoDesc(event.target.value);
                  markEdited();
                }}
              />
              <div className="sb-hint sb-hint-row">
                <span>До {SEO_DESC_MAX} символов</span>
                <span>
                  {String(seoDesc || "").length}/{SEO_DESC_MAX}
                </span>
              </div>
            </div>
            <div className="seo-preview">
              <div className="seo-url">burosoul.ru/blog/{slug || "url-stati"}</div>
              <div className="seo-title">{seoPreviewTitle}</div>
              <div className="seo-desc">{seoPreviewDesc}</div>
            </div>
          </div>

          <div className="sb-section">
            <div className="sb-section-title">Настройки</div>
            <div className="sb-toggle-row">
              <span className="sb-toggle-label">Комментарии</span>
              <div
                className={`sb-toggle ${commentsEnabled ? "on" : ""}`}
                onClick={() => {
                  setCommentsEnabled((prev) => !prev);
                  markEdited();
                }}
              />
            </div>
            <div className="sb-toggle-row">
              <span className="sb-toggle-label">Показывать автора</span>
              <div
                className={`sb-toggle ${showAuthorEnabled ? "on" : ""}`}
                onClick={() => {
                  setShowAuthorEnabled((prev) => !prev);
                  markEdited();
                }}
              />
            </div>
            <div className="sb-toggle-row">
              <span className="sb-toggle-label">Содержание</span>
              <div
                className={`sb-toggle ${tocEnabled ? "on" : ""}`}
                onClick={() => {
                  setTocEnabled((prev) => !prev);
                  markEdited();
                }}
              />
            </div>
          </div>

          <div className="sb-section">
            <div className="sb-section-title">Статистика</div>
            <div className="sb-stats">
              <div className="sb-stat">
                <div className="sb-stat-val">{wordCnt}</div>
                <div className="sb-stat-label">Слов</div>
              </div>
              <div className="sb-stat">
                <div className="sb-stat-val">~{readMinutes} мин</div>
                <div className="sb-stat-label">Чтение</div>
              </div>
              <div className="sb-stat">
                <div className="sb-stat-val">{blockCount}</div>
                <div className="sb-stat-label">Блоков</div>
              </div>
              <div className="sb-stat">
                <div className="sb-stat-val">{seoScore}</div>
                <div className="sb-stat-label">SEO</div>
              </div>
            </div>
            <div className="sb-hint">{seoText[0]} — {seoText[1]}</div>
            <div className="sb-actions">
              <button className="btn-ghost sb-action" onClick={clearDraft} disabled={submitting}>
                Очистить
              </button>
            </div>
          </div>
        </aside>
      </div>

      <div
        className={`modal-overlay ${previewOpen ? "open" : ""}`}
        onClick={(event) => {
          if (event.target === event.currentTarget) setPreviewOpen(false);
        }}
      >
        <div className="modal-box">
          <div className="modal-header">
            <div className="modal-header-title">Предпросмотр статьи</div>

            <div className="modal-header-r">
              <div className="modal-device-btns">
                <button className={`mdb ${previewDevice === "desk" ? "on" : ""}`} onClick={() => setPreviewDevice("desk")}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <rect x="2" y="3" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="1.8" />
                    <path d="M8 21h8M12 17v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                  </svg>
                  Десктоп
                </button>

                <button className={`mdb ${previewDevice === "mob" ? "on" : ""}`} onClick={() => setPreviewDevice("mob")}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <rect x="5" y="2" width="14" height="20" rx="2" stroke="currentColor" strokeWidth="1.8" />
                    <circle cx="12" cy="18" r="1" fill="currentColor" />
                  </svg>
                  Мобайл
                </button>
              </div>

              <button className="modal-close" onClick={() => setPreviewOpen(false)}>
                ✕
              </button>
            </div>
          </div>

          <div className={`modal-body ${previewDevice === "mob" ? "mobile" : ""}`}>
            <div className={previewDevice === "mob" ? "mobile-frame" : ""}>
              <div className="prev-hero">
                <div className="prev-hero-orb" />
                <div className="prev-hero-in">
                  <div className="prev-tags">
                    {previewCategory ? <span className="tag t-lav-d">{previewCategory}</span> : null}
                    <span className="tag t-ghost">{type}</span>
                  </div>

                  <div className="prev-title">{previewTitle}</div>

                  <div className="prev-author">
                    <div className="prev-av">{authorInitials}</div>
                    <div>
                      <div className="prev-aname">{authorName || "Психолог"}</div>
                      <div className="prev-aspec">Клинический психолог · Психодрама</div>
                    </div>
                  </div>

                  <div className="prev-cover">
                    {coverPreview || coverUrl ? (
                      <div className="prev-cover-img" style={{ backgroundImage: `url(${coverPreview || coverUrl})` }} />
                    ) : (
                      <div className="prev-cover-em">🖼</div>
                    )}
                  </div>
                </div>
              </div>

              <div className="prev-body">
                <div className="prev-meta">
                  <span>
                    {new Date().toLocaleDateString("ru-RU", {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    })}
                  </span>
                  <div className="prev-meta-dot" />
                  <span>{readMinutes} мин чтения</span>
                  {excerpt.trim() ? (
                    <>
                      <div className="prev-meta-dot" />
                      <span style={{ color: "var(--dark)", fontStyle: "italic" }}>{excerpt.trim()}</span>
                    </>
                  ) : null}
                </div>

                {previewArticleHtml ? (
                  <div className="prev-prose" dangerouslySetInnerHTML={{ __html: previewArticleHtml }} />
                ) : (
                  <div className="prev-empty">
                    <div className="prev-empty-ico">✍️</div>
                    <div>Начните писать статью в редакторе</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={`inline-toast ${toastShow ? "show" : ""}`}>{toastText}</div>
      {limitModal}
    </section>
  );
}
